import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import EpisodeSelector from "./components/EpisodeSelector";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { categoryLabels, movies } from "@/mocks/movies";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = useMemo(() => movies.find((m) => String(m.id) === id), [id]);
  const [episode, setEpisode] = useState(1);
  const { isFavorite, toggleFavorite } = useFavorites();

  useEffect(() => {
    setEpisode(1);
  }, [id]);

  usePageMeta(
    movie ? `${movie.title} - 91在线视频` : "影视信息暂不可用 - 91在线视频",
    movie
      ? `${movie.title}（${movie.year}）由${movie.director}执导，${movie.actors.join("、")}主演。${movie.description.slice(0, 60)}`
      : "该影视作品暂不可用，请返回首页浏览更多高清影视资源。",
  );

  if (!movie) {
    return (
      <div className="mx-auto flex w-full max-w-screen-2xl flex-col items-center justify-center gap-3 px-4 py-32 text-center md:px-8">
        <i className="ri-error-warning-line text-5xl text-accent-500" />
        <p className="text-lg font-600 text-foreground-950">影视信息暂不可用</p>
        <p className="text-sm text-foreground-500">该作品可能已被移除或链接有误</p>
        <Link
          to="/"
          className="mt-2 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-600 text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-home-5-line" />
          返回首页
        </Link>
      </div>
    );
  }

  const related = movies
    .filter((m) => m.category === movie.category && m.id !== movie.id)
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 5);

  const favorited = isFavorite(movie.id);

  const infoItems: { label: string; value: string; icon: string }[] = [
    { label: "导演", value: movie.director, icon: "ri-clapperboard-line" },
    { label: "主演", value: movie.actors.join(" / "), icon: "ri-user-star-line" },
    { label: "类型", value: movie.genres.join(" / "), icon: "ri-price-tag-3-line" },
    { label: "上映年份", value: String(movie.year), icon: "ri-calendar-line" },
    { label: "地区", value: movie.region, icon: "ri-earth-line" },
    { label: "时长", value: movie.episodes > 1 ? `每集 ${movie.duration}` : movie.duration, icon: "ri-time-line" },
  ];

  return (
    <div className="w-full pb-4">
      <div className="relative h-[280px] w-full overflow-hidden md:h-[420px]">
        <img
          src={movie.backdrop}
          alt={`${movie.title}剧照`}
          title={`${movie.title} - 91在线视频`}
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/70 to-background-50/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-background-50/90 via-background-50/40 to-transparent" />
      </div>

      <div className="mx-auto -mt-24 w-full max-w-screen-2xl px-4 md:-mt-32 md:px-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: categoryLabels[movie.category], to: `/category/${movie.category}` },
            { label: movie.title },
          ]}
        />

        <div className="mt-4 flex flex-col gap-6 lg:flex-row lg:items-start">
          <div className="relative h-[300px] w-[200px] shrink-0 overflow-hidden rounded-lg border border-background-200/70 shadow-none">
            <img
              src={movie.poster}
              alt={`${movie.title}海报`}
              title={`${movie.title} - 91在线视频`}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-600 text-background-50">
                {categoryLabels[movie.category]}
              </span>
              <span className="flex items-center gap-1 rounded-md bg-accent-500/15 px-2.5 py-1 text-xs font-600 text-accent-500">
                <i className="ri-star-fill text-[10px]" />
                {movie.rating.toFixed(1)} 分
              </span>
              <span className="flex items-center gap-1 rounded-md bg-background-200/70 px-2.5 py-1 text-xs text-foreground-600">
                <i className="ri-fire-line" />
                {movie.views}万播放
              </span>
            </div>

            <h1 className="mt-3 font-heading text-3xl font-700 text-foreground-950 md:text-4xl">
              {movie.title}
            </h1>

            <div className="mt-3 flex flex-wrap gap-2">
              {movie.genres.map((g) => (
                <Link
                  key={g}
                  to={`/category/${movie.category}`}
                  className="whitespace-nowrap rounded-full border border-background-300/60 px-3 py-1 text-xs text-foreground-600 transition-colors hover:border-accent-500 hover:text-accent-500"
                >
                  {g}
                </Link>
              ))}
            </div>

            <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
              {infoItems.map((item) => (
                <div key={item.label} className="flex items-start gap-2 text-sm">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center text-accent-500">
                    <i className={`${item.icon} text-base`} />
                  </span>
                  <span className="shrink-0 text-foreground-500">{item.label}：</span>
                  <span className="text-foreground-800">{item.value}</span>
                </div>
              ))}
            </div>

            <div className="mt-5 flex flex-wrap gap-3">
              <button
                type="button"
                disabled
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-600 text-background-50 opacity-90"
              >
                <i className="ri-play-circle-line text-lg" />
                {movie.episodes > 1 ? "开始观看（演示）" : "立即观看（演示）"}
              </button>
              <button
                type="button"
                onClick={() => toggleFavorite(movie.id)}
                className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-5 py-2.5 text-sm transition-colors ${
                  favorited
                    ? "border-accent-500 bg-accent-500/15 text-accent-500"
                    : "border-background-300/60 text-foreground-600 hover:border-accent-500 hover:text-accent-500"
                }`}
              >
                <i className={favorited ? "ri-heart-fill" : "ri-heart-line"} />
                {favorited ? "已收藏" : "收藏"}
              </button>
            </div>
          </div>
        </div>

        <section className="mt-8 rounded-lg border border-background-200/70 bg-background-100/50 p-5 md:p-6">
          <div className="mb-3 flex items-center gap-2">
            <i className="ri-file-text-line text-accent-500" />
            <h2 className="font-heading text-lg font-600 text-foreground-950">剧情简介</h2>
          </div>
          <p className="text-sm leading-relaxed text-foreground-600 md:text-base">
            {movie.description}
          </p>
        </section>

        <EpisodeSelector episodes={movie.episodes} current={episode} onChange={setEpisode} />

        <section className="mt-10">
          <div className="mb-4 flex items-center gap-2">
            <i className="ri-movie-2-line text-accent-500" />
            <h2 className="font-heading text-xl font-600 text-foreground-950">相关推荐</h2>
          </div>
          {related.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-background-300/60 py-16 text-foreground-500">
              <i className="ri-inbox-line text-4xl" />
              <p className="text-sm">暂无推荐内容</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
              {related.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          )}
        </section>

        <section className="mt-10 rounded-lg border border-background-200/70 bg-background-100 p-5 md:p-6">
          <div className="mb-3 flex items-center gap-2">
            <i className="ri-links-line text-accent-500" />
            <h2 className="font-heading text-lg font-600 text-foreground-950">所属分类</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to={`/category/${movie.category}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500/15 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50"
            >
              <i className="ri-apps-2-line" />
              {categoryLabels[movie.category]}频道
            </Link>
            <Link
              to="/favorites"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/60 px-4 py-2 text-sm text-foreground-600 transition-colors hover:border-accent-500 hover:text-accent-500"
            >
              <i className="ri-heart-line" />
              我的收藏
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}